import { collection, doc, getCountFromServer, getDocFromServer, getDocs, orderBy, query } from 'firebase/firestore';
import React, { useEffect, useState } from 'react'
import { db } from '../../firebase-config';
import { ActionIcon, Avatar, Badge, Button, Container, Group, Loader, Paper, ScrollArea, SimpleGrid, Stack, Table, rem, Text, Title } from '@mantine/core';
import { Helmet } from 'react-helmet';
import styles from './brands.module.css';
import { BrandsTable, CopyableId } from '../../components';
import { IconRefresh, IconUserShield } from '@tabler/icons-react';
import { EditBrand } from '../../components/editBrandModal/EditBrandModal';

const getStaffName = (staff) => {
    return staff.name || staff.displayName || staff.fullName || staff.email || staff.phone || 'Unnamed staff';
};

const getStaffContact = (staff) => {
    return staff.email || staff.phone || staff.username || '-';
};

const getStaffRole = (staff) => {
    if (staff.role) return staff.role;
    if (staff.permission) return staff.permission;
    if (staff.type) return staff.type;
    if (staff.manager || staff.isManager) return 'Manager';
    return 'Staff';
};

export const Brands = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [staffLoadingBrandId, setStaffLoadingBrandId] = useState('');
    const [error, setError] = useState('');
    const [openAddBrand, setOpenAddBrand] = useState(false);
    const [selectedBrandId, setSelectedBrandId] = useState('');

    const emptyBrandData = {
        name: '',
        logo: '',
        visible: false,
    };

    useEffect(() => {
        fetchLatestData();
    }, []);

    const fetchStaffProfile = async (staffDoc) => {
        const membershipData = staffDoc.data();

        try {
            const profileSnapshot = await getDocFromServer(doc(db, 'staffs', staffDoc.id));
            const profileData = profileSnapshot.exists() ? profileSnapshot.data() : {};
            return {
                ...profileData,
                ...membershipData,
                id: staffDoc.id
            };
        } catch (err) {
            return {
                ...membershipData,
                id: staffDoc.id
            };
        }
    };

    const fetchBrandStaffs = async (brandId) => {
        try {
            const staffSnapshot = await getDocs(collection(db, 'brands', brandId, 'staffs'));
            const staffs = await Promise.all(staffSnapshot.docs.map(fetchStaffProfile));
            return { staffs, staffError: '' };
        } catch (err) {
            return { staffs: [], staffError: err.message };
        }
    };

    const fetchBrandStaffCount = async (brandId) => {
        try {
            const staffCountSnapshot = await getCountFromServer(collection(db, 'brands', brandId, 'staffs'));
            return staffCountSnapshot.data().count;
        } catch (err) {
            return null;
        }
    };

    const fetchLatestData = async () => {
        setLoading(true);
        setError('');
        try {
            const querySnapshot = await getDocs(query(collection(db, 'brands'), orderBy('name')));
            const fetchedBrands = await Promise.all(querySnapshot.docs.map(async (brandDoc) => ({
                ...brandDoc.data(),
                id: brandDoc.id,
                staffs: null,
                staffError: '',
                staffCount: await fetchBrandStaffCount(brandDoc.id)
            })));

            setBrands(fetchedBrands);
            setSelectedBrandId('');
            setStaffLoadingBrandId('');
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleSelectBrand = async (brand) => {
        setSelectedBrandId(brand.id);

        if (brand.staffs !== null) {
            return;
        }

        setStaffLoadingBrandId(brand.id);
        const { staffs, staffError } = await fetchBrandStaffs(brand.id);
        setBrands((currentBrands) => currentBrands.map((currentBrand) => {
            if (currentBrand.id !== brand.id) {
                return currentBrand;
            }

            return {
                ...currentBrand,
                staffs,
                staffError,
                staffCount: staffs.length
            };
        }));
        setStaffLoadingBrandId((currentId) => currentId === brand.id ? '' : currentId);
    };

    const selectedBrand = brands.find((brand) => brand.id === selectedBrandId);

    const staffRows = selectedBrand?.staffs?.map((staff) => (
        <Table.Tr key={staff.id}>
            <Table.Td>
                <CopyableId id={staff.id} label="Staff ID" />
            </Table.Td>
            <Table.Td>
                <Text fz="sm" fw={600}>{getStaffName(staff)}</Text>
            </Table.Td>
            <Table.Td>
                <Text fz="sm" c={getStaffContact(staff) === '-' ? 'dimmed' : undefined}>
                    {getStaffContact(staff)}
                </Text>
            </Table.Td>
            <Table.Td>
                <Badge variant="light" color={getStaffRole(staff).toLowerCase().includes('manager') ? 'yellow' : 'gray'}>
                    {getStaffRole(staff)}
                </Badge>
            </Table.Td>
        </Table.Tr>
    ));

    return (
        <>
            <Helmet>
                <title>Lollet Admin - Brands</title>
            </Helmet>
            <Container size="xxl" className={styles.container}>
                {error && <Text color="red" align="center">{error}</Text>}
                {loading ? (
                    <Loader />
                ) : (
                    <Stack gap="lg">
                        <Group justify="space-between" align="flex-start">
                            <div>
                                <Title order={1}>Brands</Title>
                                <Text c="dimmed" mt={4}>
                                    Manage brand records and review staff access from one place.
                                </Text>
                            </div>
                            <Group gap="sm">
                                <Button onClick={() => setOpenAddBrand(true)}>Add Brand</Button>
                                <ActionIcon variant="light" size="lg" onClick={fetchLatestData} aria-label="Refresh brands">
                                    <IconRefresh style={{ width: rem(20), height: rem(20) }} stroke={1.5} />
                                </ActionIcon>
                            </Group>
                        </Group>

                        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg" className={styles.grid}>
                            <Paper withBorder radius="md" p="md" className={styles.panel}>
                                <Group justify="space-between" mb="md">
                                    <div>
                                        <Title order={3}>Brand Records</Title>
                                        <Text c="dimmed" fz="sm">{brands.length} total brands</Text>
                                    </div>
                                    <Badge variant="light" color="yellow">{brands.length}</Badge>
                                </Group>

                                <ScrollArea className={styles.brandsTable}>
                                    <BrandsTable
                                        data={brands}
                                        loading={loading}
                                        error={error}
                                        fetchLatestData={fetchLatestData}
                                        selectedBrandId={selectedBrandId}
                                        loadingBrandId={staffLoadingBrandId}
                                        onSelectBrand={handleSelectBrand}
                                    />
                                </ScrollArea>
                            </Paper>

                            <Paper withBorder radius="md" p="md" className={styles.panel}>
                                {selectedBrand ? (
                                    <Stack gap="md">
                                        <Group justify="space-between" align="flex-start">
                                            <Group gap="sm">
                                                <Avatar src={selectedBrand.logo} size={44} radius={8}>
                                                    <IconUserShield size={22} />
                                                </Avatar>
                                                <div>
                                                    <Title order={3}>{selectedBrand.name}</Title>
                                                    <Group gap="xs" mt={4}>
                                                        <CopyableId id={selectedBrand.id} label="Brand ID" />
                                                        <Badge color={selectedBrand.visible ? 'green' : 'gray'} variant="light">
                                                            {selectedBrand.visible ? 'Visible' : 'Hidden'}
                                                        </Badge>
                                                    </Group>
                                                </div>
                                            </Group>
                                            <Badge size="lg" variant="light" color="yellow">
                                                {staffLoadingBrandId === selectedBrand.id
                                                    ? 'Loading staffs'
                                                    : `${selectedBrand.staffCount || 0} staffs`}
                                            </Badge>
                                        </Group>

                                        <div className={styles.staffTable}>
                                            {selectedBrand.staffError && (
                                                <Text c="red" fz="sm" mb="sm">
                                                    Staffs could not be loaded: {selectedBrand.staffError}
                                                </Text>
                                            )}
                                            {staffLoadingBrandId === selectedBrand.id && selectedBrand.staffs === null ? (
                                                <Group justify="center" py="xl">
                                                    <Loader />
                                                </Group>
                                            ) : (
                                                <Table verticalSpacing="sm" striped>
                                                    <Table.Thead>
                                                        <Table.Tr>
                                                            <Table.Th>ID</Table.Th>
                                                            <Table.Th>Staff</Table.Th>
                                                            <Table.Th>Contact</Table.Th>
                                                            <Table.Th>Role</Table.Th>
                                                        </Table.Tr>
                                                    </Table.Thead>
                                                    <Table.Tbody>
                                                        {staffRows?.length ? staffRows : (
                                                            <Table.Tr>
                                                                <Table.Td colSpan={4}>
                                                                    <Text c="dimmed" ta="center" py="xl">
                                                                        No staffs found for this brand.
                                                                    </Text>
                                                                </Table.Td>
                                                            </Table.Tr>
                                                        )}
                                                    </Table.Tbody>
                                                </Table>
                                            )}
                                        </div>
                                    </Stack>
                                ) : (
                                    <Text c="dimmed" ta="center" py="xl">Select a brand to view staffs.</Text>
                                )}
                            </Paper>
                        </SimpleGrid>

                        <EditBrand 
                            brandData={emptyBrandData}
                            open={openAddBrand} 
                            setOpen={setOpenAddBrand} 
                            mode={'new'} 
                            fetchLatestData={fetchLatestData} 
                        />
                    </Stack>
                )}
            </Container>
        </>
    )
}
